import { ReactElement, useCallback, useMemo } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import { Typography } from '@mui/material';
import BigNumber from 'bignumber.js';
import { Address } from 'viem';

import { useGetUnstakePeriodQuery } from 'modules/api/actions/getUnstakePeriod';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { GuardButton } from 'modules/auth/components/GuardButton';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { Info } from 'modules/common/components/Info/Info';
import { NumberTextField } from 'modules/common/components/NumberTextField';
import { Summary } from 'modules/common/components/Summary';
import { SummaryItem } from 'modules/common/components/SummaryItem';
import { BUY_MAIN_MORE_LINK, ZERO } from 'modules/common/const';
import { useGetUSDAmount } from 'modules/common/hooks/useGetUSDAmount';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { useGetOwnerSelfStakeAmountQuery } from 'modules/ownerPanel/actions/getSelfStakeAmount';
import { useGetIsOwnerPoolQuery } from 'modules/ownerPanel/actions/isPoolOwner';
import { useGetAccountPoolQuery } from 'modules/pool/actions/getAccountPool';
import { useGetPoolQuery } from 'modules/pool/actions/getPool';
import { useGetPoolMetaQuery } from 'modules/pool/actions/getPoolMeta';
import { PoolInfoColumn } from 'modules/pool/components/PoolInfoColumn/PoolInfoColumn';

import { translation } from './translation';
import { useStyles } from './useStyles';

interface IWithdrawContentProps {
  poolAddress: Address;
  onSubmit: (params: { amount: BigNumber }) => void;
  isSubmitLoading: boolean;
}

interface IFormValues extends FieldValues {
  amount: string;
}

export function WithdrawForm({
  poolAddress,
  onSubmit,
  isSubmitLoading,
}: IWithdrawContentProps): ReactElement | null {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes } = useStyles();
  const { isConnected, connectionKey, cacheTime } = useConnection();
  const { data: pool } = useGetPoolQuery(
    { address: poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );
  const { data: poolMeta } = useGetPoolMetaQuery({ address: poolAddress });

  const { data: accountPool } = useGetAccountPoolQuery(
    { connectionKey, address: poolAddress },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );
  const { data: isOwner = false } = useGetIsOwnerPoolQuery(
    { connectionKey, poolAddress },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );
  const { data: ownerSelfStakeAmount = ZERO } = useGetOwnerSelfStakeAmountQuery(
    { poolAddress },
    {
      skip: !isConnected || !isOwner,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );

  const { data: unstakePeriodDays } = useGetUnstakePeriodQuery(
    { poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );

  const balance = useMemo(() => {
    if (!isConnected) {
      return ZERO;
    }

    if (isOwner) {
      return ownerSelfStakeAmount;
    }

    return accountPool?.stakedAmount || ZERO;
  }, [accountPool?.stakedAmount, ownerSelfStakeAmount, isConnected, isOwner]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      amount: '',
    },
  });

  const amountInputValue = watch('amount');

  const convertedAmount = useMemo(() => {
    const convertedValue = new BigNumber(amountInputValue);

    return convertedValue.isNaN() ? ZERO : convertedValue;
  }, [amountInputValue]);

  const onMaxClick = useCallback(
    () =>
      setValue('amount', balance.decimalPlaces(8).toString(), {
        shouldValidate: true,
      }),
    [balance, setValue],
  );

  const totalStakedAmount = useMemo(() => {
    const newTotalStaked = balance.minus(convertedAmount);

    if (newTotalStaked.isLessThan(ZERO)) {
      return ZERO;
    }

    return newTotalStaked;
  }, [balance, convertedAmount]);
  const totalStakedAmountUSD = useGetUSDAmount(totalStakedAmount);

  if (!pool) {
    return null;
  }

  const isSubmitDisabled = !!errors.amount?.message || !watch('amount');

  return (
    <form
      className={classes.root}
      onSubmit={handleSubmit(({ amount }) =>
        onSubmit({ amount: new BigNumber(amount) }),
      )}
    >
      <Typography
        mb={1}
        textAlign="center"
        textTransform="uppercase"
        variant="h2"
      >
        {t(keys.withdraw)}
      </Typography>

      <PoolInfoColumn
        address={poolAddress}
        imageIpfsLink={poolMeta?.ipfsImageLink}
        name={poolMeta?.name}
        type="withdraw"
      />

      <NumberTextField
        balance={balance}
        className={classes.input}
        control={control as unknown as Control<FieldValues>}
        error={!!errors.amount?.message}
        getMoreLink={BUY_MAIN_MORE_LINK}
        label={t(keys.inputTokenLabel, { token: t(keys.tokens.main) })}
        name="amount"
        placeholder="0"
        unit={t(keys.tokens.main)}
        variant="filled"
        onMaxClick={onMaxClick}
      />

      <div className={classes.content}>
        <div className={classes.infos}>
          {unstakePeriodDays && (
            <Info>
              {t(keys.period, { value: unstakePeriodDays.toNumber() })}
            </Info>
          )}
        </div>
      </div>

      <Summary className={classes.summary}>
        <SummaryItem
          label={t(keys.totalAmountLabel)}
          usdValue={totalStakedAmountUSD}
          value={t(keys.unit.tokenValue, {
            value: totalStakedAmount.decimalPlaces(8),
            token: t(keys.tokens.main),
          })}
        />
      </Summary>

      <GuardButton
        disabled={isSubmitDisabled}
        loading={isSubmitLoading}
        size="large"
        sx={theme => ({ marginTop: theme.spacing(6) })}
        type="submit"
      >
        {t(keys.withdraw)}
      </GuardButton>
    </form>
  );
}
